'use client';

import {
  NavigationMenu as DefaultNavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger
} from '@/components/ui/navigation-menu';
import NavigationLink from '@/components/NavigationLink';
import Menu from '@/components/globals/Header/Menu';
import { useHeaderData, useIsActive, usePathnameWithoutLocal } from '@/components/globals/Header/HeaderDataProvider';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useParams } from 'next/navigation';
import { useState } from 'react';

const Column = props => {
  const { label, children = [], onClose } = props;
  const pathnameWithoutLocal = usePathnameWithoutLocal();

  return (
    <div className='flex flex-col gap-4 py-7'>
      {label && <p className='text-lg border-b border-zinc-100 pb-1 mx-2 xl:mx-4'>{label}</p>}
      <div className='flex flex-col gap-2'>
        {children.map((child, index) => (
          <div
            key={index}
            className={cn(
              'flex flex-col gap-2 hover:bg-destructive-foreground p-2 xl:p-4 cursor-pointer group transition-colors select-none',
              {
                'bg-destructive-foreground pointer-events-none opacity-50': !child.link || child.link === '#'
              }
            )}
          >
            <NavigationLink href={child.link} onClick={onClose}>
              {child.title && (
                <p
                  className={cn('text-lg font-semibold group-hover:text-secondary transition-colors mb-2 ', {
                    'text-secondary': child.link?.includes(pathnameWithoutLocal) && pathnameWithoutLocal.length > 1
                  })}
                >
                  {child.title}
                </p>
              )}
              {child.subtitle && <p className='text-xs xl:text-sm'>{child.subtitle}</p>}
            </NavigationLink>
          </div>
        ))}
      </div>
    </div>
  );
};

const LastColumn = () => {
  const { lastColumn } = useHeaderData();
  const { locale } = useParams();

  const { title, subtitle, cta, note } = lastColumn;

  return (
    <div className='bg-destructive-foreground col-start-4 ml-4 xl:ml-6'>
      <div className='flex flex-col gap-8 py-7 mx-6 xl:mx-8'>
        {title && <p className='text-lg border-b border-zinc-200 pb-1'>{title}</p>}
        {subtitle && <div className='prose' dangerouslySetInnerHTML={{ __html: subtitle }} />}
        <div className='flex flex-col gap-4 mt-8'>
          {cta && !locale.startsWith('fr') && <Button {...cta} variant='secondary' size='md' />}
          {note && <div className='text-base link-secondary prose' dangerouslySetInnerHTML={{ __html: note }} />}
        </div>
      </div>
    </div>
  );
};

const Item = props => {
  const { link, label, columns = [], onClose } = props;

  const showMoreIcon = !!columns.length;

  const isActive = useIsActive({ columns, link });

  if (!showMoreIcon)
    return (
      <NavigationMenuItem>
        <NavigationLink href={link} legacyBehavior passHref>
          <NavigationMenuLink
            className={cn('hover:text-primary active:text-primary text-sm xl:text-base font-medium', {
              'text-primary': isActive
            })}
          >
            {label}
          </NavigationMenuLink>
        </NavigationLink>
      </NavigationMenuItem>
    );

  return (
    <NavigationMenuItem>
      <NavigationMenuTrigger
        className={cn(
          '!bg-transparent hover:text-primary px-0 w-fit focus:text-current text-sm xl:text-base [&_svg]:ml-1.5 xl:[&_svg]:ml-2.5',
          {
            'text-primary': isActive
          }
        )}
      >
        {label}
      </NavigationMenuTrigger>
      <NavigationMenuContent>
        <ScrollArea classNames={{ viewport: "h-fit" }}>
          <div className='grid grid-cols-[repeat(3,1fr)_1.2fr] pl-4 xl:pl-6 gap-2 xl:gap-4 w-[var(--navigation-menu-width)] left-0 max-h-[calc(100vh-var(--header-max-height))]'>
            {columns.map((column, index) => (
              <Column key={index} {...column} onClose={onClose} />
            ))}
            <LastColumn />
          </div>
        </ScrollArea>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
};

export default function NavigationMenu(props) {
  const { links = [] } = props;
  const [open, setOpen] = useState('');

  return (
    <>
      <DefaultNavigationMenu value={open} onValueChange={setOpen} className='hidden lg:block'>
        <NavigationMenuList className='gap-3 xl:gap-5 mx-6'>
          {links.map((item, index) => (
            <Item key={index} {...item} onClose={() => setOpen('')} />
          ))}
        </NavigationMenuList>
      </DefaultNavigationMenu>
      <Menu links={links} />
    </>
  );
}
